import { useState } from "react";
import { SYSTEM_VARIABLES, type ContractRole, type Currency, type CustomVariableDef, type VariableType } from "@talentkontor/domain";
import { adoptFromLibrary, libraryAdoptable, libraryRemove, libraryUpsert } from "./bibliothek";
import { ROLE_LABEL } from "./modell";
import { WertFeld } from "./WertFeld";
import { currentLang, nameIn, t } from "../../../lib/sprache";

/** Passt ein Systemwert zum Typ der Angabe (für die Vorbelegung)? */
function compatible(own: VariableType, sys: VariableType): boolean {
  if (own === "text" || own === "lines") return sys === "text" || sys === "lines";
  return own === sys;
}

const TYPES: { key: VariableType; label: string }[] = [
  { key: "text", label: "Text" }, { key: "lines", get label() { return t("Mehrzeiliger Text", "Multi-line text"); } }, { key: "money", get label() { return t("Betrag", "Amount"); } }, { key: "date", get label() { return t("Datum", "Date"); } },
  { key: "number", get label() { return t("Zahl", "Number"); } }, { key: "percent", get label() { return t("Prozent", "Percent"); } }, { key: "boolean", get label() { return t("Ja/Nein", "Yes/No"); } },
];
const KEY = /^[a-z][a-z0-9_]{0,63}$/;

export function slugKey(label: string): string {
  const base = label.toLowerCase().replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").replace(/^[^a-z]+/, "");
  return (base || "angabe").slice(0, 40);
}

/** Eigene Angaben einer Vorlage: Schlüssel, Bezeichnung DE/EN, Typ, Pflicht, Standardwert, wer beim Unterschreiben einträgt, Auswahl. */
export function VariablenEditor({ defs, onChange, disabled, signers, currency = "EUR", library, onLibraryChange }: {
  defs: CustomVariableDef[];
  onChange: (d: CustomVariableDef[]) => void;
  disabled?: boolean;
  signers: ContractRole[];
  currency?: Currency;
  /** Bibliothek eigener Angaben der Agentur (PARITAET-18); ohne sie kein Übernehmen */
  library?: CustomVariableDef[];
  /** Nur der Admin pflegt die Bibliothek */
  onLibraryChange?: (lib: CustomVariableDef[]) => void;
}) {
  const update = (i: number, patch: Partial<CustomVariableDef>) => onChange(defs.map((d, k) => (k === i ? { ...d, ...patch } : d)));
  const duplicates = new Set(defs.map((d) => d.key).filter((k, i, a) => a.indexOf(k) !== i));
  const adoptable = library ? libraryAdoptable(library, defs) : [];
  const [pick, setPick] = useState("");
  const picked = adoptable.find((d) => d.key === pick) ?? adoptable[0] ?? null;
  return (
    <div className="stack">
      {library && (library.length > 0 || onLibraryChange) && (
        <div className="stack tk-gap-sm">
          <strong>{t("Bibliothek der Agentur", "Agency library")}</strong>
          {adoptable.length > 0 ? (
            <div className="row">
              <label>
                {t("Angabe aus der Bibliothek", "Field from the library")}<select value={picked?.key ?? ""} onChange={(e) => setPick(e.target.value)} disabled={disabled}>
                  {adoptable.map((d) => <option key={d.key} value={d.key}>{(currentLang() === "en" && d.label.en) || d.label.de} ({d.key})</option>)}
                </select>
              </label>
              {!disabled && picked && <button type="button" onClick={() => onChange(adoptFromLibrary(defs, picked))}>{t("In die Vorlage übernehmen", "Add to template")}</button>}
              {onLibraryChange && picked && <button type="button" onClick={() => onLibraryChange(libraryRemove(library, picked.key))}>{t("Aus der Bibliothek entfernen", "Remove from library")}</button>}
            </div>
          ) : (
            <small>{library.length ? t("Alle Angaben der Bibliothek stehen schon in dieser Vorlage.", "All library fields are already in this template.")
              : t("Noch leer. Eine Angabe unten mit „In der Bibliothek speichern“ ablegen; danach steht sie in jeder Vorlage zur Auswahl.", "Still empty. Store a field below with “Save to library”; it is then available in every template.")}</small>
          )}
        </div>
      )}
      {defs.map((d, i) => {
        const keyProblem = !KEY.test(d.key) ? t("Nur Kleinbuchstaben, Ziffern und _; Beginn mit Buchstaben.", "Only lowercase letters, digits and _; must start with a letter.") : duplicates.has(d.key) ? t("Dieser Schlüssel kommt doppelt vor.", "This key appears twice.") : null;
        return (
          <fieldset key={i} className="panel stack tk-var-def">
            <legend>{d.label.de || t(`Angabe ${i + 1}`, `Field ${i + 1}`)} <span className="tk-mono">{`{{var.${d.key}}}`}</span></legend>
            <div className="grid">
              <label>
                {t("Bezeichnung (Deutsch)", "Label (German)")}<input value={d.label.de} disabled={disabled} onChange={(e) => update(i, { label: { ...d.label, de: e.target.value }, ...(d.key.startsWith("angabe") || !d.key ? { key: slugKey(e.target.value) } : {}) })} />
              </label>
              <label>
                {t("Bezeichnung (Englisch)", "Label (English)")}<input value={d.label.en} disabled={disabled} onChange={(e) => update(i, { label: { ...d.label, en: e.target.value } })} />
              </label>
            </div>
            <div className="grid">
              <label>
                {t("Schlüssel", "Key")}<input className="tk-mono" value={d.key} disabled={disabled} aria-invalid={keyProblem ? true : undefined} onChange={(e) => update(i, { key: e.target.value.trim() })} />
                {keyProblem && <small className="tk-error">{keyProblem}</small>}
              </label>
              <label>
                {t("Art", "Type")}<select value={d.type} disabled={disabled} onChange={(e) => update(i, { type: e.target.value as VariableType, default: undefined })}>
                  {TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </label>
            </div>
            <div className="grid">
              <label>
                {t("Trägt beim Unterschreiben ein", "Filled in on signing by")}<select value={d.fillable_by ?? ""} disabled={disabled} onChange={(e) => update(i, { fillable_by: (e.target.value || undefined) as ContractRole | undefined })}>
                  <option value="">{t("Niemand (die Agentur trägt ein)", "Nobody (the agency fills it in)")}</option>
                  {(["brand", "talent"] as ContractRole[]).map((r) => <option key={r} value={r} disabled={!signers.includes(r)}>{ROLE_LABEL[r]}{signers.includes(r) ? "" : t(" (unterschreibt nicht)", " (does not sign)")}</option>)}
                </select>
              </label>
              {d.type === "boolean" ? (
                <label>
                  {t("Standardwert (optional)", "Default value (optional)")}<select value={d.default === true ? "ja" : d.default === false ? "nein" : ""} disabled={disabled} onChange={(e) => update(i, { default: e.target.value === "" ? undefined : e.target.value === "ja" })}>
                    <option value="">{t("Kein Standard", "No default")}</option><option value="ja">{t("Ja", "Yes")}</option><option value="nein">{t("Nein", "No")}</option>
                  </select>
                </label>
              ) : (
                // Dasselbe Eingabefeld wie im Vertrag: Beträge wie 1.500,00, Datum mit Kalender (Befund 26)
                <WertFeld key={`${d.key}-${d.type}`} def={{ ...d, key: `default-${i}-${d.key}`, label: { de: "Standardwert", en: "Default" }, required: false, fillable_by: undefined, options: d.options }}
                  value={d.default ?? undefined} currency={currency} disabled={disabled} onChange={(v) => update(i, { default: v === "" || v === undefined ? undefined : v })} />
              )}
            </div>
            {d.type !== "boolean" && (
              <label>
                {t("Aus den Stammdaten vorbelegen (optional)", "Pre-fill from master data (optional)")}<select value={d.prefill ?? ""} disabled={disabled} onChange={(e) => update(i, { prefill: e.target.value || undefined })}>
                  <option value="">{t("Nicht vorbelegen", "Do not pre-fill")}</option>
                  {SYSTEM_VARIABLES.filter((v) => compatible(d.type, v.type)).map((v) => <option key={v.path} value={v.path}>{nameIn(v.label.de, v.label.en)}</option>)}
                </select>
                <small>{t("Beim Anlegen eines Vertrags trägt die App den Wert aus den Stammdaten ein; weicht er später ab, weist die Prüfung darauf hin.", "When a contract is created, the app enters the value from the master data; if it later differs, the review points it out.")}</small>
              </label>
            )}
            {d.type === "text" && (
              <label>
                {t("Auswahl (optional, durch Komma getrennt)", "Options (optional, separated by commas)")}<input value={(d.options ?? []).join(", ")} disabled={disabled} onChange={(e) => update(i, { options: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} />
              </label>
            )}
            <div className="row between">
              <label className="row"><input type="checkbox" checked={d.required} disabled={disabled} onChange={(e) => update(i, { required: e.target.checked })} />{t("Pflichtangabe", "Mandatory field")}</label>
              <div className="row">
                {onLibraryChange && library && KEY.test(d.key) && d.label.de.trim() && !duplicates.has(d.key) && (
                  <button type="button" className="tk-bib-knopf" onClick={() => onLibraryChange(libraryUpsert(library, d))}>
                    {library.some((x) => x.key === d.key) ? t("In der Bibliothek aktualisieren", "Update in library") : t("In der Bibliothek speichern", "Save to library")}
                  </button>
                )}
                {!disabled && <button type="button" onClick={() => onChange(defs.filter((_, k) => k !== i))}>{t("Angabe entfernen", "Remove field")}</button>}
              </div>
            </div>
          </fieldset>
        );
      })}
      {defs.length === 0 && <p>{t("Noch keine eigenen Angaben. Systemangaben (Agentur, Creator, Marke, Kooperation) stehen immer zur Verfügung.", "No own fields yet. System fields (agency, creator, brand, deal) are always available.")}</p>}
      {!disabled && (
        <button type="button" onClick={() => onChange([...defs, { key: `angabe_${defs.length + 1}`, label: { de: "", en: "" }, type: "text", required: true }])}>{t("Eigene Angabe hinzufügen", "Add own field")}</button>
      )}
    </div>
  );
}
